import React, { useEffect, useRef, useState } from 'react';
import { Paper, Typography, FormControl, FormHelperText, CircularProgress, Button } from '@mui/material';
import InputField from 'packages/novous_web/src/common/components/InputField';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import CButton from 'packages/novous_web/src/common/components/Button';
import { useNavigate } from 'react-router-dom';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useDebounce from 'packages/novous_web/src/hooks/useDebounce';
import car from 'packages/novous_web/src/assets/images/car.svg';
import truck from 'packages/novous_web/src/assets/images/truck.svg';
import minivan from 'packages/novous_web/src/assets/images/minivan.svg';
import bus from 'packages/novous_web/src/assets/images/bus.svg';

type DataProps = {
  data: any;
};

type FormErrors = {
  deviceType?: string;
  deviceId?: string;
  groupName?: string;
  vehicleRegistrationNo?: string;
  vehicleType?: string;
  fuelDeviceId?: string;
  dsmCameraId?: string;
  inCabinCameraId?: string;
  rearCameraId?: string;
  dsmCameraPosition?: string;
  rearCameraPosition?: string;
  // add others if needed
};

const AddDeviceForm: React.FC<DataProps> = ({ data }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    deviceType: 'fcw_dsm',
    deviceId: '',
    fuelDeviceId: '',
    groupName: '',
    vehicleRegistrationNo: '',
    dsmCameraId: '',
    inCabinCameraId: '',
    rearCameraId: '',
    rearCameraPosition: '1',
    dsmCameraPosition: '1',
    vehicleType: '',
  });
  const { showToast } = useToast();
  const [formErrors, setFormErrors] = React.useState<FormErrors>({});
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonHeight, setButtonHeight] = useState(45);
  const [isFormFilled, setIsFormFilled] = useState(false);
  const debouncedVehicleRegistrationNumber = useDebounce(formData.vehicleRegistrationNo, 1000);
  const debouncedDeviceId = useDebounce(formData.deviceId, 1000);

  // Add status states for visual feedback
  const [deviceIdStatus, setDeviceIdStatus] = useState<'success' | 'error' | ''>('');
  const [regNoStatus, setRegNoStatus] = useState<'success' | 'error' | ''>('');
  const [deviceIdValid, setDeviceIdValid] = useState(false);
  const [regNoValid, setRegNoValid] = useState(false);

  const {
    data: checkDeviceExistData,
    fetchData: checkDeviceExist,
    loading: deviceCheckLoading,
    error: deviceCheckError,
  } = useAxios<any>(`${API_ENDPOINTS.directories.VEHICLE_DEVICE_EXIST}/${debouncedDeviceId}`, 'POST');

  const {
    data: checkVehicleExistData,
    fetchData: checkVehicleExist,
    loading: vehicleCheckLoading,
    error: vehicleCheckError,
  } = useAxios<any>(
    `${API_ENDPOINTS.directories.VEHICLE_REGISTRATION_EXIST}/${debouncedVehicleRegistrationNumber}`,
    'POST',
  );

  useEffect(() => {
    if (!data && debouncedVehicleRegistrationNumber) {
      checkVehicleExist();
    }
  }, [debouncedVehicleRegistrationNumber]);

  const cameraPosition = [
    { label: 'NONE', value: '1' },
    { label: 'RIGHT SIDE CAMERA', value: '2' },
    { label: 'LEFT SIDE CAMERA', value: '3' },
    { label: 'PASSENGER COUNTING', value: '4' },
  ];

  useEffect(() => {
    if (data) {
      setFormData((prev) => ({
        ...prev,
        deviceId: data.deviceId || '',
        fuelDeviceId: data.fuelDeviceId || '',
        deviceType: data.deviceType || 'fcw_dsm',
        groupName: data.groupName || '',
        dsmCameraId: data.dsmCameraId || '',
        inCabinCameraId: data.inCabinCameraId || '',
        rearCameraId: data.rearCameraId || '',
        dsmCameraPosition: data.dsmCameraPosition || '1',
        rearCameraPosition: data.rearCameraPosition || '1',
        vehicleRegistrationNo: data.vehicleRegistrationNo || '',
        vehicleType: data.vehicleType || '',
      }));
      setSelectedVehicle(data.vehicleType || '');
      // Set status for edit mode
      setDeviceIdStatus('success');
      setRegNoStatus('success');
      setDeviceIdValid(true);
      setRegNoValid(true);
    }
  }, [data]);

  useEffect(() => {
    if (!data && debouncedDeviceId) {
      checkDeviceExist();
    }
  }, [debouncedDeviceId]);

  useEffect(() => {
    if (checkDeviceExistData && checkDeviceExistData?.data) {
      setDeviceIdValid(false);
      setDeviceIdStatus('error');
      setFormErrors((prev) => ({
        ...prev,
        deviceId: 'Device Already Mapped',
      }));
    } else if (debouncedDeviceId && !deviceCheckLoading && !deviceCheckError) {
      setDeviceIdValid(true);
      setDeviceIdStatus('success');
      setFormErrors((prev) => ({ ...prev, deviceId: '' }));
    } else if (!debouncedDeviceId) {
      setDeviceIdStatus('');
      setDeviceIdValid(false);
    }

    if (deviceCheckError) {
      setDeviceIdStatus('error');
      setFormErrors((prev) => ({
        ...prev,
        deviceId: 'Error checking device',
      }));
    }
  }, [checkDeviceExistData, deviceCheckError, deviceCheckLoading, debouncedDeviceId]);

  useEffect(() => {
    if (checkVehicleExistData && checkVehicleExistData?.data) {
      setRegNoValid(false);
      setRegNoStatus('error');
      setFormErrors((prev) => ({
        ...prev,
        vehicleRegistrationNo: 'Vehicle Already Mapped',
      }));
    } else if (debouncedVehicleRegistrationNumber && !vehicleCheckLoading && !vehicleCheckError) {
      setRegNoValid(true);
      setRegNoStatus('success');
      setFormErrors((prev) => ({ ...prev, vehicleRegistrationNo: '' }));
    } else if (!debouncedVehicleRegistrationNumber) {
      setRegNoStatus('');
      setRegNoValid(false);
    }

    if (vehicleCheckError) {
      setRegNoStatus('error');
      setFormErrors((prev) => ({
        ...prev,
        vehicleRegistrationNo: 'Error checking vehicle',
      }));
    }
  }, [checkVehicleExistData, vehicleCheckError, vehicleCheckLoading, debouncedVehicleRegistrationNumber]);

  const {
    data: deviceAddData,
    error: deviceAddError,
    loading: deviceAddLoading,
    fetchData: addDevice,
  } = useAxios<any>(`${API_ENDPOINTS.directories.ADD_DEVICE}`, 'POST');

  const {
    data: deviceEditData,
    error: deviceEditError,
    loading: deviceEditLoading,
    fetchData: EditDevice,
  } = useAxios<any>(`${API_ENDPOINTS.directories.EDIT_DEVICE}`, 'POST');
  const {
    data: groupsData,
    error: groupsError,
    loading: groupsLoading,
    fetchData: fetchGroupsData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id}`, 'GET');

  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(data?.vehicleType || '');

  const vehicleOptions = [
    { label: 'Car', value: 'CAB', image: car },
    { label: 'Minivan', value: 'MINBUS', image: minivan },
    { label: 'Truck', value: 'TRUCK', image: truck },
    { label: 'Bus', value: 'BUS', image: bus },
  ];

  const handleVehicleIconSelect = (type: any) => setFormData((prev) => ({ ...prev, vehicleType: type }));

  const handleSelect = (vehicle: string) => {
    setSelectedVehicle(vehicle);
    handleVehicleIconSelect(vehicle);
    setFormErrors((prev) => ({ ...prev, vehicleType: validateField('vehicleType', vehicle) }));
  };

  useEffect(() => {
    if (user?.customer_id) {
      fetchGroupsData();
    }
  }, [user?.customer_id]);
  useEffect(() => {
    if (buttonRef.current) setButtonHeight(buttonRef.current.offsetHeight);
  }, []);

  // Block + - = @ for specific fields
  const BLOCKED_CHARS = /[+\=@]/;

  const validateField = (name: string, value: string): string => {
    const fieldLabels: { [key: string]: string } = {
      deviceId: 'Serial Number',
      fuelDeviceId: 'Fuel Device ID',
      groupName: 'Group',
      vehicleRegistrationNo: 'Vehicle Registration No',
      vehicleType: 'Vehicle Type',
    };

    if (!value && ['deviceId', 'groupName', 'vehicleRegistrationNo', 'vehicleType'].includes(name))
      return `${fieldLabels[name]} is required.`;

    if (
      (name === 'deviceId' || name === 'vehicleRegistrationNo' || name === 'fuelDeviceId') &&
      BLOCKED_CHARS.test(value)
    ) {
      return `${fieldLabels[name]} cannot contain + - = @`;
    }
    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Reset validation status when user starts typing
    if (name === 'deviceId') {
      setDeviceIdStatus('');
      setDeviceIdValid(false);
    } else if (name === 'vehicleRegistrationNo') {
      setRegNoStatus('');
      setRegNoValid(false);
    }

    if (
      (name === 'deviceId' || name === 'vehicleRegistrationNo' || name === 'fuelDeviceId') &&
      BLOCKED_CHARS.test(value)
    ) {
      const msg =
        name === 'deviceId'
          ? 'Serial Number cannot contain special characters'
          : name === 'vehicleRegistrationNo'
          ? 'Vehicle Registration No cannot contain + = @'
          : 'Fuel Device ID cannot contain + - = @';

      setFormErrors((prev) => ({ ...prev, [name]: msg }));
      if (name === 'deviceId') setDeviceIdStatus('error');
      if (name === 'vehicleRegistrationNo') setRegNoStatus('error');
      if (name === 'fuelDeviceId') setRegNoStatus('error');
      return;
    }

    if (/\s/.test(value)) {
      return '';
    }
    if (name === 'deviceId' && /^[A-Za-z0-9-]*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
    if (name === 'fuelDeviceId' && /^[A-Za-z0-9-]*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
    if (name === 'vehicleRegistrationNo' || name === 'deviceId') {
      if (value && /^[a-zA-Z0-9\-_]+$/.test(value)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: '' }));
      }
      setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
    if (name === 'dsmCameraId' || name === 'inCabinCameraId' || name === 'rearCameraId') {
      if (value && /^[a-zA-Z0-9\-_]+$/.test(value)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleDropdownChange = (name: string, value: string) => {
    console.log('Dropdown Change:', name, value);

    if (name === 'deviceType') {
      setFormData({
        deviceType: 'fcw_dsm',
        deviceId: '',
        fuelDeviceId: '',
        groupName: '',
        vehicleRegistrationNo: '',
        dsmCameraId: '',
        inCabinCameraId: '',
        rearCameraId: '',
        rearCameraPosition: '1',
        dsmCameraPosition: '1',
        vehicleType: '',
      });
      setFormErrors({});
      setDeviceIdStatus('');
      setRegNoStatus('');
      setDeviceIdValid(false);
      setRegNoValid(false);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleAddDevice = () => {
    // let errors: { [key: string]: string } = {};

    // // Validate required fields based on device type
    // ['deviceId', 'groupName', 'vehicleRegistrationNo', 'vehicleType'].forEach((field) => {
    //   const errorMsg = validateField(field, formData[field as keyof typeof formData]);
    //   if (errorMsg) errors[field] = errorMsg;
    // });

    // setFormErrors(errors);

    // if (Object.keys(errors).length > 0) return; // Prevent API call if errors exist

    const errors: FormErrors = {};

    if (!formData.deviceType) {
      errors.deviceType = 'Please select Device Type';
    }
    if (!formData.deviceId) {
      errors.deviceId = 'Please enter Device Id';
    }
    if (!formData.groupName) {
      errors.groupName = 'Please select Depot Name';
    }
    if (!formData.vehicleRegistrationNo) {
      errors.vehicleRegistrationNo = 'Please enter Vehicle Registration No';
    }
    if (!selectedVehicle) {
      errors.vehicleType = 'Please select Vehicle Type';
    }

    // Add other validations as needed based on your component logic

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return; // stop submitting if errors exist
    }

    const payload: any = {
      vehicleRegistrationNo: formData.vehicleRegistrationNo,
      deviceId: formData.deviceId,
      groupName: formData.groupName,
      deviceType: formData.deviceType,
      customerId: user?.customer_id,
      parentId: user?.parentId,
      dsmCameraPosition: parseInt(formData.dsmCameraPosition, 10),
      rearCameraPosition: parseInt(formData.rearCameraPosition, 10),
      vehicleType: formData.vehicleType,
    };

    if (formData.fuelDeviceId) {
      payload.fuelDeviceId = formData.fuelDeviceId;
    }

    if (formData.deviceType === 'fcw_dsm') {
      payload.dsmCameraId = formData.dsmCameraId;
      payload.inCabinCameraId = formData.inCabinCameraId;
      payload.rearCameraId = formData.rearCameraId;
    }

    console.log('Payload:', payload);
    if (!data) {
      addDevice(payload);
    } else {
      EditDevice(payload);
    }
  };

  useEffect(() => {
    const requiredFields = ['deviceId', 'groupName', 'vehicleRegistrationNo', 'vehicleType'];
    const areRequiredFieldsFilled = requiredFields.every(
      (field) => formData[field as keyof typeof formData]?.trim() !== '',
    );

    const hasNoErrors = Object.values(formErrors).every((error) => !error);

    if (areRequiredFieldsFilled && hasNoErrors && deviceIdValid && regNoValid) {
      setIsFormFilled(true);
    } else {
      setIsFormFilled(false);
    }
  }, [formData, formErrors, deviceIdValid, regNoValid]);

  const ResetState = () => {
    setFormData({
      deviceType: 'fcw_dsm',
      deviceId: '',
      fuelDeviceId: '',
      groupName: '',
      vehicleRegistrationNo: '',
      dsmCameraId: '',
      inCabinCameraId: '',
      rearCameraId: '',
      rearCameraPosition: '1',
      dsmCameraPosition: '1',
      vehicleType: '',
    });
    setDeviceIdStatus('');
    setRegNoStatus('');
    setDeviceIdValid(false);
    setRegNoValid(false);
  };

  useEffect(() => {
    if (deviceAddData?.message) showToast(deviceAddData?.message, deviceAddData?.data);
    if (deviceAddData?.data === 'success') navigate(-1);
    ResetState();

    return () => {
      ResetState();
    };
  }, [deviceAddData]);

  useEffect(() => {
    if (deviceEditData?.message) showToast(deviceEditData?.message, deviceEditData?.data);
    if (deviceEditData?.data === 'success') {
      navigate(-1);
    }
    ResetState();

    return () => {
      ResetState();
    };
  }, [deviceEditData]);

  useEffect(() => {
    if (deviceAddError) showToast(deviceAddError, 'error');
  }, [deviceAddError]);

  useEffect(() => {
    if (deviceEditError) showToast(deviceEditError, 'error');
  }, [deviceEditError]);

  return (
    <Paper elevation={0} className="flex flex-col h-full relative">
      <div
        className="flex-1 gap-2 overflow-y-auto"
        style={{ maxHeight: `calc(100vh - 180px - ${buttonHeight}px)` }}
      >
        <Typography variant="h6">{data ? 'Edit' : 'Add'} a New Device</Typography>

        <form className="flex flex-col gap-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Device Type */}
            <FormControl fullWidth>
              <CustomDropDownInput
                label="Device Type"
                isMandatory={true}
                options={[
                  { value: 'fcw_dsm', label: 'FCW-DMS' },
                  { value: 'fcw', label: 'FCW' },
                  { value: 'dsm', label: 'DMS' },
                  { value: 'vts', label: 'VTS' },
                ]}
                value={formData.deviceType}
                isDisabled={data}
                onChange={(value) => handleDropdownChange('deviceType', value)}
                placeholder="Select Device Type"
              />
              <FormHelperText>{formErrors.deviceType}</FormHelperText>
            </FormControl>
          </div>
          <Typography variant="h6">{formData?.deviceType?.toUpperCase().replace(/_/g, '-')}</Typography>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Serial Number */}
            <FormControl fullWidth error={!!formErrors.deviceId}>
              <InputField
                label="Unique device Serial number"
                name="deviceId"
                isMandatory={true}
                isDisabled={data}
                value={formData.deviceId}
                onChange={handleChange}
                placeholder="Enter Serial Number"
                errorMessage={formErrors.deviceId}
                status={deviceIdStatus || (data ? 'success' : 'error')}
              />
            </FormControl>

            {(formData.deviceType === 'fcw_dsm' || formData.deviceType === 'vts') && (
              <FormControl fullWidth error={!!formErrors.fuelDeviceId}>
                <InputField
                  label="Fuel Device ID (optional)"
                  name="fuelDeviceId"
                  isMandatory={false}
                  value={formData.fuelDeviceId}
                  onChange={handleChange}
                  placeholder="Enter Fuel Device ID"
                  errorMessage={formErrors.fuelDeviceId}
                />
              </FormControl>
            )}
            {formData.deviceType === 'fcw_dsm' && (
              <>
                <FormControl fullWidth error={!!formErrors.dsmCameraId}>
                  <InputField
                    label="DSM Camera ID"
                    name="dsmCameraId"
                    value={formData.dsmCameraId}
                    onChange={handleChange}
                    placeholder="Enter DSM Camera ID"
                  />
                  <FormHelperText>{formErrors.dsmCameraId}</FormHelperText>
                </FormControl>
                {/* In Cabin Camera ID */}
                <FormControl fullWidth error={!!formErrors.inCabinCameraId}>
                  <InputField
                    label="In Cabin Camera ID"
                    name="inCabinCameraId"
                    value={formData.inCabinCameraId}
                    onChange={handleChange}
                    placeholder="Enter Cabin Camera ID"
                  />
                  <FormHelperText>{formErrors.inCabinCameraId}</FormHelperText>
                </FormControl>
                {/* Rear Camera ID */}
                <FormControl fullWidth error={!!formErrors.rearCameraId}>
                  <InputField
                    label="Rear Camera ID"
                    name="rearCameraId"
                    value={formData.rearCameraId}
                    onChange={handleChange}
                    placeholder="Enter Rear Camera ID"
                  />
                  <FormHelperText>{formErrors.rearCameraId}</FormHelperText>
                </FormControl>

                {/* Dsm Camera Position */}
                <FormControl fullWidth>
                  <CustomDropDownInput
                    label="Select Dsm Camera Position"
                    options={cameraPosition}
                    value={formData.dsmCameraPosition}
                    onChange={(value) => handleDropdownChange('dsmCameraPosition', value)}
                    placeholder="Select Dsm Camera Position"
                  />
                </FormControl>
                {/* Rear Camera Position */}
                <FormControl fullWidth>
                  <CustomDropDownInput
                    label="Select Rear Camera Position"
                    options={cameraPosition}
                    value={formData.rearCameraPosition}
                    onChange={(value) => handleDropdownChange('rearCameraPosition', value)}
                    placeholder="Select Rear Camera Position"
                  />
                </FormControl>
              </>
            )}

            <FormControl fullWidth error={!!formErrors.groupName}>
              <CustomDropDownInput
                label="Depot"
                isMandatory={true}
                options={
                  groupsData?.data?.length
                    ? groupsData?.data?.map((item: string) => ({
                        value: item,
                        label: item,
                      }))
                    : []
                }
                value={formData.groupName}
                onChange={(value) => handleDropdownChange('groupName', value)}
                placeholder="Select Depot"
              />
              <FormHelperText>{formErrors.groupName}</FormHelperText>
            </FormControl>

            <FormControl fullWidth error={!!formErrors.vehicleRegistrationNo}>
              <InputField
                label="Vehicle Registration No"
                name="vehicleRegistrationNo"
                isMandatory={true}
                isDisabled={data}
                value={formData.vehicleRegistrationNo}
                onChange={handleChange}
                status={regNoStatus || (data ? 'success' : 'error')}
                placeholder="Enter Vehicle Registration No"
                errorMessage={formErrors.vehicleRegistrationNo}
              />
            </FormControl>

            <div className="mb-6">
              <Typography
                variant="subtitle1"
                style={{
                  fontFamily: 'Inter Tight',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '27px',
                  letterSpacing: '0%',
                  marginBottom: '20px',
                }}
              >
                Choose Vehicle Icon{' '}
                <span className="text-red-500" title="Required field">
                  *
                </span>
              </Typography>
              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                {vehicleOptions.map((option) => (
                  <div key={option.value} className="flex flex-col items-center">
                    <Button
                      variant="outlined"
                      onClick={() => handleSelect(option.value)}
                      style={{
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: selectedVehicle === option.value ? '2px solid blue' : '1px solid gray',
                        borderRadius: '8px',
                        // opacity: isEdit ? 0.5 : 1,
                        // cursor: isEdit ? "not-allowed" : "pointer",
                      }}
                      // disabled={isEdit}
                    >
                      <img src={option.image} alt={option.label} style={{ width: '32px', height: '32px' }} />
                    </Button>
                    <span className="mt-2 text-sm font-semibold text-gray-700">{option.label}</span>
                  </div>
                ))}
              </div>
              {formErrors.vehicleType && <FormHelperText error>{formErrors.vehicleType}</FormHelperText>}
            </div>
          </div>
        </form>
      </div>

      <div className="mt-6 w-full">
        <div className="flex gap-2 items-center justify-between" ref={buttonRef}>
          <CustomIconButton
            text="Cancel"
            background="transparent"
            outline={true}
            padding="0px"
            variant="outlined"
            onClick={() => navigate(-1)}
          />
          <CButton
            text={data ? 'Edit Device' : `Add Device`}
            fullWidth={false}
            onClick={handleAddDevice}
            // disabled={!isFormFilled || deviceCheckLoading || vehicleCheckLoading}
            isLoading={deviceAddLoading || deviceEditLoading}
          />
        </div>
      </div>
    </Paper>
  );
};

export default AddDeviceForm;
